// SAD-859: who is on a team. A team's members log its work products; the
// Program Lead (anyone with delivery rights on the program) manages the list.
// People come from the SAD-832 picker source: the tenant's contacts merged
// with its platform users, each person once.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraEmptyState, SpecraFormField, SpecraPersonPicker, SpecraSectionHeader, SpecraSelect, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useAuth } from '../../../../contexts/authContext';
import { authClient } from '../../../../lib/connect/clients';
import { mergePeopleForPicker, parsePersonKey } from '../../Programs/tabs/personPicker';
import { contactHooks } from '../../Registries/hooks';
import { useCmAccess } from '../access';
import { useAddWorkstreamMember, useRemoveWorkstreamMember, useUpdateWorkstreamMember, useWorkstreamMembers } from '../hooks';
export function usePeopleDirectory({ enabled = true } = {}) {
    const { user } = useAuth();
    const tenantId = user?.tenant_id ?? '';
    const contactsQuery = contactHooks.useList({
        companyId: tenantId
    });
    const usersQuery = useQuery({
        queryKey: [
            'delivery:team-member-directory',
            tenantId
        ],
        enabled: enabled && !!tenantId,
        staleTime: 60000,
        queryFn: async ()=>{
            const resp = await authClient.listUsersForSharing({
                query: ''
            });
            return resp.users.map((u)=>({
                    userId: u.id,
                    name: u.name,
                    email: u.email
                }));
        }
    });
    return useMemo(()=>{
        const contacts = contactsQuery.data?.items ?? [];
        const users = usersQuery.data ?? [];
        const people = mergePeopleForPicker(contacts, users);
        const contactName = new Map(contacts.map((c)=>[
                c.id,
                `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim() || c.email || 'Unnamed contact'
            ]));
        const nameByUser = new Map();
        for (const u of users)nameByUser.set(u.userId, u.name?.trim() || u.email || 'A user');
        for (const c of contacts)if (c.platformUserId) nameByUser.set(c.platformUserId, contactName.get(c.id) ?? '');
        const userName = (id)=>id === user?.id ? 'You' : nameByUser.get(id) ?? 'Someone on the program';
        return {
            people,
            userName,
            memberName: (m)=>m.contactId && contactName.get(m.contactId) || m.userId && nameByUser.get(m.userId) || m.effectiveUserId && nameByUser.get(m.effectiveUserId) || 'Someone on the program',
            isLoading: contactsQuery.isLoading || usersQuery.isLoading
        };
    }, [
        contactsQuery.data,
        contactsQuery.isLoading,
        usersQuery.data,
        usersQuery.isLoading,
        user?.id
    ]);
}
/**
 * Whether the signed-in user may log and edit a team's work products: a
 * member of the team, or anyone with delivery rights on the program. ryugu
 * decides the same way; this only hides controls that would be refused.
 */ export function useTeamWriter(programId) {
    const { user } = useAuth();
    const access = useCmAccess(programId);
    const members = useWorkstreamMembers(programId);
    return useMemo(()=>{
        const mine = new Set((members.data ?? []).filter((m)=>!!user?.id && m.effectiveUserId === user.id).map((m)=>m.workstreamId));
        return {
            isLoading: access.isLoading || members.isLoading,
            /** Program-wide: may log for any team, assess and promote. */ runsDelivery: access.canWrite,
            canLogFor: (wsId)=>access.canWrite || mine.has(wsId)
        };
    }, [
        access.canWrite,
        access.isLoading,
        members.data,
        members.isLoading,
        user?.id
    ]);
}
export function TeamMembersPanel({ programId, wsId }) {
    const access = useCmAccess(programId);
    const members = useWorkstreamMembers(programId, wsId);
    const dir = usePeopleDirectory();
    const add = useAddWorkstreamMember(programId, {
        success: 'Added to the team'
    });
    const setRole = useUpdateWorkstreamMember(programId, {
        success: 'Role updated'
    });
    const remove = useRemoveWorkstreamMember(programId, {
        success: 'Removed from the team'
    });
    const [picked, setPicked] = useState();
    const [role, setRoleDraft] = useState('member');
    const rows = useMemo(()=>[
            ...members.data ?? []
        ].sort((a, b)=>(a.role === 'lead' ? 0 : 1) - (b.role === 'lead' ? 0 : 1) || dir.memberName(a).localeCompare(dir.memberName(b))), [
        members.data,
        dir
    ]);
    const onTeam = useMemo(()=>{
        const s = new Set();
        for (const m of rows){
            if (m.contactId) s.add(`contact:${m.contactId}`);
            if (m.userId) s.add(`user:${m.userId}`);
            if (m.effectiveUserId) s.add(`user:${m.effectiveUserId}`);
        }
        return s;
    }, [
        rows
    ]);
    const options = dir.people.filter((p)=>!onTeam.has(p.key)).map((p)=>({
            id: p.key,
            name: p.label,
            description: p.kind === 'user' ? 'Platform user' : undefined
        }));
    const addPicked = ()=>{
        const parsed = picked ? parsePersonKey(picked.id) : null;
        if (!parsed) return;
        add.mutate({
            workstreamId: wsId,
            role,
            ...parsed.kind === 'contact' ? {
                contactId: parsed.id
            } : {
                userId: parsed.id
            }
        }, {
            onSuccess: ()=>{
                setPicked(undefined);
                setRoleDraft('member');
            }
        });
    };
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-4",
        "data-testid": Testids.cmWorkProducts.membersPanel,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-1",
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        variant: "group",
                        title: `On this team · ${rows.length}`
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        size: "caption",
                        tone: "muted",
                        children: "Members log the work this team produced. The lead is who the PM asks about it."
                    })
                ]
            }),
            access.canWrite ? /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-end gap-2",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "min-w-64 flex-1",
                        children: /*#__PURE__*/ _jsx(SpecraPersonPicker, {
                            label: "Person",
                            people: options,
                            selectedPerson: picked,
                            onPersonChange: setPicked,
                            placeholder: "Pick from your contacts or your organization",
                            loading: dir.isLoading,
                            triggerProps: {
                                'data-testid': Testids.cmWorkProducts.memberPicker
                            }
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Role",
                        children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                            value: role,
                            onChange: (e)=>setRoleDraft(e.target.value),
                            "data-testid": Testids.cmWorkProducts.memberRole,
                            children: [
                                /*#__PURE__*/ _jsx("option", {
                                    value: "member",
                                    children: "Member"
                                }),
                                /*#__PURE__*/ _jsx("option", {
                                    value: "lead",
                                    children: "Lead"
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        disabled: !picked || add.isPending,
                        onClick: addPicked,
                        "data-testid": Testids.cmWorkProducts.memberAdd,
                        children: "Add to team"
                    })
                ]
            }) : null,
            rows.length === 0 && !members.isLoading ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                size: "compact",
                title: "Nobody is on this team yet",
                description: access.canWrite ? 'Add the people who do this work, so they can log what they produce.' : 'The Program Lead adds the people who do this work.'
            }) : /*#__PURE__*/ _jsx("ul", {
                className: "divide-y divide-border",
                children: rows.map((m)=>/*#__PURE__*/ _jsxs("li", {
                        className: "flex items-center justify-between gap-3 py-2",
                        "data-testid": Testids.cmWorkProducts.memberRow,
                        "data-row-id": m.id,
                        children: [
                            /*#__PURE__*/ _jsxs("span", {
                                className: "flex items-center gap-2",
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraText, {
                                        as: "span",
                                        className: "font-medium",
                                        children: dir.memberName(m)
                                    }),
                                    m.role === 'lead' ? /*#__PURE__*/ _jsx(SpecraBadge, {
                                        variant: "info",
                                        children: "Lead"
                                    }) : null,
                                    !m.effectiveUserId ? /*#__PURE__*/ _jsx(SpecraText, {
                                        as: "span",
                                        size: "caption",
                                        tone: "muted",
                                        title: "This contact has no login, so they cannot log work themselves.",
                                        children: "No login"
                                    }) : null
                                ]
                            }),
                            access.canWrite ? /*#__PURE__*/ _jsxs("span", {
                                className: "flex gap-1",
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        variant: "ghost",
                                        disabled: setRole.isPending,
                                        onClick: ()=>setRole.mutate({
                                                memberId: m.id,
                                                role: m.role === 'lead' ? 'member' : 'lead'
                                            }),
                                        "data-testid": Testids.cmWorkProducts.memberLead,
                                        children: m.role === 'lead' ? 'Make member' : 'Make lead'
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        variant: "ghost",
                                        disabled: remove.isPending,
                                        onClick: ()=>remove.mutate({
                                                memberId: m.id
                                            }),
                                        "data-testid": Testids.cmWorkProducts.memberRemove,
                                        children: "Remove"
                                    })
                                ]
                            }) : null
                        ]
                    }, m.id))
            })
        ]
    });
}
